/**
 * PlaytestMode — Overlay that takes over the DungeonEditor for mechanical testing.
 * WASD/arrow movement, fog of war, trigger log, active flags.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { RotateCcw, X } from 'lucide-react';
import type {
  GridMap,
  GridMapState,
  Entity,
  Trigger,
  TriggerEffect,
  TriggerEvent,
  MoveDirection,
} from '@omen-quest/grid-map';
import {
  createGridMapState,
  applyInput,
  DEFAULT_RENDER_CONFIG,
} from '@omen-quest/grid-map';
import { useDungeonEditorStore } from '@/store/dungeonEditorStore';
import { buildGridFromMapData } from '@/lib/game-logic/grid-builder';
import type { DungeonTrigger, DungeonMapData } from '@/lib/db/dungeon-types';

// ============================================================================
// HELPERS
// ============================================================================

function buildTriggerEffect(t: DungeonTrigger): TriggerEffect {
  switch (t.effect) {
    case 'dialogue':
      return {
        type: 'dialogue',
        text: (t.effectData.text as string) || '',
        speaker: t.effectData.speaker as string | undefined,
      };
    case 'combat':
      return {
        type: 'combat',
        encounterId: (t.effectData.encounterId as string) || '',
      };
    case 'trap':
      return {
        type: 'trap',
        damage: (t.effectData.damage as number) || 0,
        description: (t.effectData.description as string) || '',
      };
    case 'teleport':
      return {
        type: 'teleport',
        targetX: (t.effectData.targetX as number) || 0,
        targetY: (t.effectData.targetY as number) || 0,
      };
    case 'custom':
      return {
        type: 'custom',
        eventName: (t.effectData.eventName as string) || '',
        data: t.effectData,
      };
    default:
      return { type: 'dialogue', text: 'Unknown trigger' };
  }
}

function buildGridMap(mapData: DungeonMapData, playerSpawn: { x: number; y: number }): GridMap {
  const cells = buildGridFromMapData(mapData);

  const player: Entity = {
    id: 'playtest-player',
    x: playerSpawn.x,
    y: playerSpawn.y,
    type: 'player',
    name: 'Player',
    icon: '@',
    lightRadius: 8,
  };

  const dungeonEntities: Entity[] = mapData.entities.map((e) => ({
    id: e.id,
    x: e.x,
    y: e.y,
    type: e.type as Entity['type'],
    name: e.name,
    icon: e.icon,
    metadata: e.metadata,
  }));

  const triggers: Trigger[] = mapData.triggers
    .filter((t) => t.targetX !== undefined && t.targetY !== undefined)
    .map((t) => ({
      id: t.id,
      cells: [{ x: t.targetX!, y: t.targetY! }],
      condition: t.condition,
      effect: buildTriggerEffect(t),
      oneShot: t.oneShot,
      enabled: true,
      requiredFlags: t.requiredFlags,
      setsFlag: t.setsFlag,
      visible: true,
      icon: '!',
      color: '#a855f7',
    }));

  return {
    width: mapData.gridWidth,
    height: mapData.gridHeight,
    cells,
    entities: [player, ...dungeonEntities],
    triggers,
    name: 'Playtest',
  };
}

function findSpawnPoint(mapData: DungeonMapData): { x: number; y: number } {
  const entrance = mapData.rooms.find((r) => r.purpose === 'entrance') ?? mapData.rooms[0];
  if (entrance) {
    return {
      x: Math.floor(entrance.x + entrance.width / 2),
      y: Math.floor(entrance.y + entrance.height / 2),
    };
  }
  // Fallback: find first floor cell
  return { x: 1, y: 1 };
}

// ============================================================================
// CANVAS RENDERER
// ============================================================================

const TILE = 20;
const COLORS = DEFAULT_RENDER_CONFIG.colors;

function renderPlaytest(
  canvas: HTMLCanvasElement,
  state: GridMapState,
  containerWidth: number,
  containerHeight: number,
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const { map, fogGrid, lightGrid, openDoors, playerId } = state;

  // Find player for camera centering
  const player = map.entities.find((e) => e.id === playerId);
  if (!player) return;

  // Center camera on player
  const camX = containerWidth / 2 - player.x * TILE - TILE / 2;
  const camY = containerHeight / 2 - player.y * TILE - TILE / 2;

  canvas.width = containerWidth;
  canvas.height = containerHeight;

  ctx.clearRect(0, 0, containerWidth, containerHeight);
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, containerWidth, containerHeight);

  ctx.save();
  ctx.translate(camX, camY);

  // Draw cells
  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width; x++) {
      const fog = fogGrid[y]?.[x] ?? 'hidden';
      if (fog === 'hidden') continue;

      const cell = map.cells[y]?.[x];
      if (!cell) continue;

      const light = lightGrid[y]?.[x] ?? 0;
      const px = x * TILE;
      const py = y * TILE;

      // Terrain color
      let color: string;
      switch (cell.terrain) {
        case 'floor':
          color = COLORS.floor;
          break;
        case 'door': {
          const key = `${x},${y}`;
          color = openDoors.has(key) ? COLORS.doorOpen : COLORS.door;
          break;
        }
        case 'stairs_up':
          color = COLORS.stairsUp;
          break;
        case 'stairs_down':
          color = COLORS.stairsDown;
          break;
        case 'water':
          color = COLORS.water;
          break;
        case 'wall':
          color = COLORS.wall;
          break;
        default:
          color = COLORS.void;
      }

      ctx.fillStyle = color;
      ctx.fillRect(px, py, TILE, TILE);

      // Wall edges on floor cells
      if (cell.terrain === 'floor' || cell.terrain === 'door') {
        ctx.strokeStyle = COLORS.wallEdge;
        ctx.lineWidth = 1.5;
        if (cell.walls.N) { ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(px + TILE, py); ctx.stroke(); }
        if (cell.walls.S) { ctx.beginPath(); ctx.moveTo(px, py + TILE); ctx.lineTo(px + TILE, py + TILE); ctx.stroke(); }
        if (cell.walls.W) { ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(px, py + TILE); ctx.stroke(); }
        if (cell.walls.E) { ctx.beginPath(); ctx.moveTo(px + TILE, py); ctx.lineTo(px + TILE, py + TILE); ctx.stroke(); }
      }

      // Explored fog overlay
      if (fog === 'explored') {
        ctx.fillStyle = COLORS.fogExplored;
        ctx.fillRect(px, py, TILE, TILE);
      } else if (fog === 'visible' && light < 0.5) {
        // Dim light tint
        ctx.fillStyle = `rgba(0,0,0,${0.5 - light * 0.5})`;
        ctx.fillRect(px, py, TILE, TILE);
      }
    }
  }

  // Draw visible trigger indicators
  const triggers = map.triggers ?? [];
  for (const trigger of triggers) {
    if (!trigger.visible) continue;
    if (state.firedTriggers.has(trigger.id)) continue;
    for (const cell of trigger.cells) {
      const fog = fogGrid[cell.y]?.[cell.x] ?? 'hidden';
      if (fog === 'hidden') continue;
      const px = cell.x * TILE;
      const py = cell.y * TILE;
      ctx.fillStyle = trigger.color ?? '#a855f7';
      ctx.globalAlpha = 0.5;
      ctx.fillRect(px + 2, py + 2, TILE - 4, TILE - 4);
      ctx.globalAlpha = 1;
      ctx.fillStyle = trigger.color ?? '#a855f7';
      ctx.font = `bold ${TILE - 6}px monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(trigger.icon ?? '!', px + TILE / 2, py + TILE / 2);
    }
  }

  // Draw entities (non-player)
  for (const entity of map.entities) {
    if (entity.id === playerId) continue;
    const fog = fogGrid[entity.y]?.[entity.x] ?? 'hidden';
    if (fog === 'hidden') continue;

    const px = entity.x * TILE;
    const py = entity.y * TILE;

    let tokenColor: string;
    switch (entity.type) {
      case 'npc': tokenColor = COLORS.npcToken; break;
      case 'monster': tokenColor = COLORS.monsterToken; break;
      default: tokenColor = COLORS.objectToken;
    }

    ctx.fillStyle = tokenColor;
    ctx.beginPath();
    ctx.arc(px + TILE / 2, py + TILE / 2, TILE / 2 - 2, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#fff';
    ctx.font = `${TILE - 8}px monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(entity.icon ?? entity.name[0] ?? '?', px + TILE / 2, py + TILE / 2);
  }

  // Draw player last (always visible)
  const px = player.x * TILE;
  const py = player.y * TILE;
  ctx.fillStyle = COLORS.playerToken;
  ctx.beginPath();
  ctx.arc(px + TILE / 2, py + TILE / 2, TILE / 2 - 1, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#1a1a1a';
  ctx.font = `bold ${TILE - 6}px monospace`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('@', px + TILE / 2, py + TILE / 2);

  ctx.restore();
}

// ============================================================================
// TRIGGER LOG ENTRY TYPE
// ============================================================================

interface TriggerLogEntry {
  id: string;
  triggerId: string;
  name: string;
  effectType: string;
  flagSet?: string;
  timestamp: number;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function PlaytestMode() {
  const mapData = useDungeonEditorStore((s) => s.mapData);
  const setPlaytesting = useDungeonEditorStore((s) => s.setPlaytesting);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef<GridMapState | null>(null);
  const animFrameRef = useRef<number>(0);

  const [triggerLog, setTriggerLog] = useState<TriggerLogEntry[]>([]);
  const [activeFlags, setActiveFlags] = useState<string[]>([]);
  const [, forceRender] = useState(0);

  // --------------------------------------------------------------------------
  // Initialize state
  // --------------------------------------------------------------------------
  const initState = useCallback(() => {
    const spawn = findSpawnPoint(mapData);
    const gridMap = buildGridMap(mapData, spawn);
    try {
      const gmState = createGridMapState(gridMap, 'playtest-player');
      stateRef.current = gmState;
      setTriggerLog([]);
      setActiveFlags([...gmState.triggerFlags]);
      forceRender((n) => n + 1);
    } catch (err) {
      console.error('[PlaytestMode] Failed to init state:', err);
    }
  }, [mapData]);

  useEffect(() => {
    initState();
  }, [initState]);

  // --------------------------------------------------------------------------
  // Render loop
  // --------------------------------------------------------------------------
  useEffect(() => {
    let running = true;

    function frame() {
      if (!running) return;
      const canvas = canvasRef.current;
      const container = containerRef.current;
      const state = stateRef.current;
      if (canvas && container && state) {
        renderPlaytest(canvas, state, container.clientWidth, container.clientHeight);
      }
      animFrameRef.current = requestAnimationFrame(frame);
    }

    animFrameRef.current = requestAnimationFrame(frame);
    return () => {
      running = false;
      cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  // --------------------------------------------------------------------------
  // Process pending events → update log + flags
  // --------------------------------------------------------------------------
  const processPendingEvents = useCallback((events: TriggerEvent[], state: GridMapState) => {
    if (events.length === 0) return;

    const newEntries: TriggerLogEntry[] = events.map((ev) => {
      // Find trigger name from map
      const trigger = state.map.triggers?.find((t) => t.id === ev.triggerId);
      return {
        id: `${ev.triggerId}-${Date.now()}-${Math.random()}`,
        triggerId: ev.triggerId,
        name: trigger?.setsFlag
          ? `${ev.triggerId} → sets "${trigger.setsFlag}"`
          : ev.triggerId,
        effectType: ev.effect.type,
        flagSet: trigger?.setsFlag,
        timestamp: Date.now(),
      };
    });

    setTriggerLog((prev) => [...newEntries, ...prev].slice(0, 100));
    setActiveFlags([...state.triggerFlags]);
  }, []);

  // --------------------------------------------------------------------------
  // Keyboard input
  // --------------------------------------------------------------------------
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      let direction: MoveDirection | null = null;

      switch (e.key) {
        case 'ArrowUp':
        case 'w':
        case 'W':
          direction = 'up';
          break;
        case 'ArrowDown':
        case 's':
        case 'S':
          direction = 'down';
          break;
        case 'ArrowLeft':
        case 'a':
        case 'A':
          direction = 'left';
          break;
        case 'ArrowRight':
        case 'd':
        case 'D':
          direction = 'right';
          break;
        default:
          return;
      }

      e.preventDefault();

      if (!stateRef.current || !direction) return;

      const newState = applyInput(stateRef.current, { type: 'move', direction });
      stateRef.current = newState;

      if (newState.pendingEvents.length > 0) {
        processPendingEvents(newState.pendingEvents, newState);
      }

      forceRender((n) => n + 1);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [processPendingEvents]);

  // --------------------------------------------------------------------------
  // Escape to exit
  // --------------------------------------------------------------------------
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPlaytesting(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setPlaytesting]);

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------
  return (
    <div className="absolute inset-0 z-40 flex bg-gray-950">
      {/* Map area */}
      <div ref={containerRef} className="flex-1 relative overflow-hidden">
        <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

        {/* Controls hint */}
        <div className="absolute bottom-3 left-3 text-xs text-gray-500 select-none pointer-events-none">
          WASD / Arrow Keys to move · Esc to exit
        </div>
      </div>

      {/* Sidebar */}
      <aside className="w-[280px] flex flex-col bg-gray-900 border-l border-gray-700 shrink-0">
        {/* Header */}
        <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-700 shrink-0">
          <span className="text-xs font-bold tracking-widest text-purple-400 uppercase flex-1">
            Playtest
          </span>
          <button
            title="Reset"
            onClick={initState}
            className="p-1 rounded text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
          >
            <RotateCcw size={14} />
          </button>
          <button
            title="Exit playtest (Esc)"
            onClick={() => setPlaytesting(false)}
            className="p-1 rounded text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* Active flags */}
        <div className="px-3 py-2 border-b border-gray-700 shrink-0">
          <p className="text-xs font-semibold text-gray-400 mb-1.5">Active Flags</p>
          {activeFlags.length === 0 ? (
            <p className="text-xs text-gray-600 italic">None</p>
          ) : (
            <div className="flex flex-wrap gap-1">
              {activeFlags.map((flag) => (
                <span
                  key={flag}
                  className="px-1.5 py-0.5 rounded text-xs bg-purple-900/60 text-purple-300 border border-purple-700/50"
                >
                  {flag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Trigger log */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <p className="sticky top-0 bg-gray-900 px-3 pt-2 pb-1.5 text-xs font-semibold text-gray-400 border-b border-gray-800">
            Trigger Log
          </p>
          {triggerLog.length === 0 ? (
            <p className="px-3 py-3 text-xs text-gray-600 italic">
              No triggers fired yet. Move the player with WASD.
            </p>
          ) : (
            <ul className="divide-y divide-gray-800">
              {triggerLog.map((entry) => (
                <li key={entry.id} className="px-3 py-2">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-xs font-mono text-gray-200 truncate flex-1">
                      {entry.triggerId}
                    </span>
                    <span className="shrink-0 px-1 py-0.5 rounded text-[10px] bg-gray-700 text-gray-300">
                      {entry.effectType}
                    </span>
                  </div>
                  {entry.flagSet && (
                    <p className="text-[11px] text-purple-400">
                      sets &ldquo;{entry.flagSet}&rdquo;
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}
